/**
 * Hit testing utilities for markup shapes
 * Determines if a point intersects with a shape's stroke or tag
 * DOES NOT hit test the interior/fill area of shapes
 */

import type { MarkupShape } from '@/types/markup.types';
import { transformShapeForViewBox, imageYToViewBoxY } from './markupRenderer';

/**
 * Calculate tag position and check if point hits the tag
 * Tags are circular labels with shape numbers
 */
function hitTestTag(
  x: number,
  y: number,
  shape: MarkupShape,
  viewBoxHeight: number,
  tolerance: number = 2
): boolean {
  const smallerDimension = Math.min(100, viewBoxHeight);
  const labelRadius = smallerDimension * 0.03;
  const labelOffset = labelRadius + 1;

  let labelX: number;
  let labelY: number;

  if (shape.type === 'rectangle' || shape.type === 'spotlight') {
    // Calculate image center and rectangle center
    const imageCenterX = 50;
    const imageCenterY = viewBoxHeight / 2;
    const rectCenterX = shape.geometry.x + shape.geometry.width / 2;
    const rectCenterY = shape.geometry.y + shape.geometry.height / 2;

    // Find which edge is closest to image center
    const dx = imageCenterX - rectCenterX;
    const dy = imageCenterY - rectCenterY;

    if (Math.abs(dx) > Math.abs(dy)) {
      // Horizontal edges are closer
      if (dx > 0) {
        labelX = shape.geometry.x + shape.geometry.width + labelOffset;
        labelY = rectCenterY;
      } else {
        labelX = shape.geometry.x - labelOffset;
        labelY = rectCenterY;
      }
    } else {
      // Vertical edges are closer
      if (dy > 0) {
        labelX = rectCenterX;
        labelY = shape.geometry.y + shape.geometry.height + labelOffset;
      } else {
        labelX = rectCenterX;
        labelY = shape.geometry.y - labelOffset;
      }
    }
  } else if (shape.type === 'circle') {
    // Position label on circumference toward image center
    const imageCenterX = 50;
    const imageCenterY = viewBoxHeight / 2;
    const dx = imageCenterX - shape.geometry.cx;
    const dy = imageCenterY - shape.geometry.cy;
    const angle = Math.atan2(dy, dx);
    const labelDistance = shape.geometry.r + labelOffset;
    labelX = shape.geometry.cx + labelDistance * Math.cos(angle);
    labelY = shape.geometry.cy + labelDistance * Math.sin(angle);
  } else if (shape.type === 'arrow') {
    // Label at midpoint of arrow line, slightly offset upward
    labelX = (shape.geometry.x1 + shape.geometry.x2) / 2;
    labelY = (shape.geometry.y1 + shape.geometry.y2) / 2 - 2;
  } else {
    return false;
  }

  // Check if click is within tag circle
  const distance = Math.sqrt(Math.pow(x - labelX, 2) + Math.pow(y - labelY, 2));
  return distance <= labelRadius + tolerance;
}

/**
 * Check if a point (in percentage coordinates) hits a shape's stroke or tag
 * Returns the shape number if hit, null otherwise
 *
 * Note: We use shape.number as the identifier since IDs are optional.
 * The number is unique within a critique's shapes (1-9 for @-mentions).
 *
 * BUG FIX (#480): Transform shapes to viewBox space before hit testing
 * to match rendered positions
 */
export function hitTestShapes(
  x: number,
  y: number,
  shapes: MarkupShape[],
  viewBoxHeight: number,
  tolerance: number = 2 // percentage units for stroke hit testing
): number | null {
  // Calculate photo aspect ratio from viewBoxHeight
  const photoAspectRatio = 100 / viewBoxHeight;

  // Transform click coordinates from image space to viewBox space
  const clickYInViewBox = imageYToViewBoxY(y, photoAspectRatio);

  // Check shapes in reverse order (top to bottom in z-order)
  for (let i = shapes.length - 1; i >= 0; i--) {
    const shape = shapes[i];

    // Transform shape to viewBox space to match rendered positions
    const transformedShape = transformShapeForViewBox(shape, photoAspectRatio);

    // First, check if clicking on the tag (most precise target)
    if (hitTestTag(x, clickYInViewBox, transformedShape, viewBoxHeight, 2)) {
      return shape.number;
    }

    if (transformedShape.type === 'rectangle' || transformedShape.type === 'spotlight') {
      // ONLY check near rectangle edges (NOT interior)
      const { x: rx, y: ry, width, height } = transformedShape.geometry;

      // Check if near any edge (using transformed click Y coordinate)
      const nearLeft = x >= rx - tolerance && x <= rx + tolerance && clickYInViewBox >= ry - tolerance && clickYInViewBox <= ry + height + tolerance;
      const nearRight = x >= rx + width - tolerance && x <= rx + width + tolerance && clickYInViewBox >= ry - tolerance && clickYInViewBox <= ry + height + tolerance;
      const nearTop = clickYInViewBox >= ry - tolerance && clickYInViewBox <= ry + tolerance && x >= rx - tolerance && x <= rx + width + tolerance;
      const nearBottom = clickYInViewBox >= ry + height - tolerance && clickYInViewBox <= ry + height + tolerance && x >= rx - tolerance && x <= rx + width + tolerance;

      if (nearLeft || nearRight || nearTop || nearBottom) {
        return shape.number;
      }
    } else if (transformedShape.type === 'circle') {
      // ONLY check near circle circumference (NOT interior)
      const { cx, cy, r } = transformedShape.geometry;
      const distance = Math.sqrt(Math.pow(x - cx, 2) + Math.pow(clickYInViewBox - cy, 2));

      // Check if distance is approximately equal to radius (on the stroke)
      if (Math.abs(distance - r) <= tolerance) {
        return shape.number;
      }
    } else if (transformedShape.type === 'arrow') {
      // Check if point is near arrow line
      const { x1, y1, x2, y2 } = transformedShape.geometry;

      // Distance from point to line segment (using transformed click Y coordinate)
      const lineLength = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
      if (lineLength === 0) continue;

      const t = Math.max(0, Math.min(1, ((x - x1) * (x2 - x1) + (clickYInViewBox - y1) * (y2 - y1)) / (lineLength * lineLength)));
      const projX = x1 + t * (x2 - x1);
      const projY = y1 + t * (y2 - y1);
      const distance = Math.sqrt(Math.pow(x - projX, 2) + Math.pow(clickYInViewBox - projY, 2));

      if (distance <= tolerance) {
        return shape.number;
      }
    }
  }

  return null;
}
